import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { GatsbyImage, getImage, IGatsbyImageData } from 'gatsby-plugin-image';

import { fixPropWarnings } from '../utils/fixPropWarnings';

import ButtonOrLink from './ButtonOrLink';

type ProductLinkVariant = 'primary' | 'secondary';

const ProductText = styled.div.withConfig(fixPropWarnings('variant'))<Pick<ProductLinkProps, 'variant'>>`
  background: ${({ variant }) => variant === 'secondary' ? '  #EBEDEB' : '#2b2c32'};
  padding: 4px 20px;
  min-height: 44px;
  transition: background-color 200ms ease, color 200ms ease;

  p {
    font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
    margin: 0;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: ${({ variant }) => variant === 'secondary'
    ? themeGet('colors.black')
    : themeGet('colors.white')};
    text-align: center;
    inline-size: min-content;
    margin: 0 auto;
  }
`;

const ProductImg = styled(GatsbyImage)`
  transition: all 300ms ease;
  object-fit: contain;
  width: 100%;
  max-width: 168px;
`;

const ProductLinkWrapper = styled(ButtonOrLink).withConfig(fixPropWarnings('variant'))<Pick<ProductLinkProps, 'variant'>>`
  &:hover,
  &:focus {
    ${ProductText} {
      background: ${({ variant }) => variant === 'secondary' ? '#EBEDEB' : themeGet('colors.velvet')};

      p {
        font-weight: 600;
        color: ${({ variant }) => variant === 'secondary'
    ? themeGet('colors.black')
    : themeGet('colors.white')};
      }
    }

    ${ProductImg} {
      transform: scale(1.1);
    }
  }
`;

const Product = styled.div.withConfig(fixPropWarnings('variant'))<Pick<ProductLinkProps, 'variant'>>`
  border-radius: 12px;
  overflow: hidden;
  background: ${({ variant }) => variant === 'secondary' ? '  #EBEDEB' : '#f7f7f7'};
  display: flex;
  flex-direction: column;
  margin: 0 6px;
  min-width: 134px;
  height: 100%;
  filter: drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.1));
`;

const ProductContainer = styled.div`
  background-color: ${themeGet('colors.white')};
  padding: 8px 4px 6px;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
`;

interface ProductLinkProps {
  className?: string;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  link: any;
  variant?: ProductLinkVariant;
}

const ProductLink = ({
  className,
  variant = 'primary',
  link,
}: ProductLinkProps) => {
  if (!link?.linkUrl || !link?.linkText) return null;
  return (
    <ProductLinkWrapper variant={variant} key={link.id} href={link.linkUrl}>
      <Product variant={variant} className={className}>
        <ProductContainer>
          <ProductImg
            image={
              getImage(link.iconDamAsset.asset.localImage) as IGatsbyImageData
            }
            objectFit="contain"
            alt={link.iconDamAsset?.altText ?? ''}
          />
        </ProductContainer>
        <ProductText variant={variant}>
          <p>{link.linkText}</p>
        </ProductText>
      </Product>
    </ProductLinkWrapper>
  );
};

export default ProductLink;
